class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      // Get current cart state to check for associated products
      const currentCart = await cartItems.getCartState();
      const itemIndex = parseInt(this.dataset.index) - 1;
      const itemToRemove = currentCart.items[itemIndex];

      console.log('[CartRemoveButton] Current cart state:', currentCart);
      console.log('[CartRemoveButton] Removing item at index:', itemIndex, itemToRemove);

      if (itemToRemove) {
        // Check if this item has associated free products
        const associatedProducts = await cartItems.getAssociatedProductsToRemove(itemToRemove, currentCart);

        if (associatedProducts.length > 0) {
          console.log('[CartRemoveButton] Found associated products to remove:', associatedProducts);
          // Remove all associated products in a single request
          await cartItems.removeMultipleItems([itemIndex + 1, ...associatedProducts]);
        } else {
          // No associated products, just remove the single item
          cartItems.updateQuantity(this.dataset.index, 0);
        }
      } else {
        // Fallback to original behavior
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

class ClearCart extends HTMLElement {
  constructor() {
    super();

    async function clearCart() {
      console.log('Clearing cart');
      await fetch(`${window.Shopify.routes.root}cart/clear.js`, { method: 'POST' })
        .then(() => {
          console.log('Your cart has been cleared!');
          location.reload();
        })
        .catch((error) => {
          console.error('Error clearing cart:', error);
        });
    }

    this.addEventListener('click', (event) => {
      clearCart();
    });
  }
}

customElements.define('clear-cart', ClearCart);

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.instanceId = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    this.lineItemStatusElement = document.getElementById('CartDrawer-LineItemStatus');
    this.setupQuantitySelectors();
    this.setupCartUpdateListener();
  }

  // Helper function to update sections with new markup
  updateSectionsWithMarkup(sectionsData) {
    console.log('[CartItems] Updating sections with markup:', sectionsData);

    Object.entries(sectionsData).forEach(([sectionId, html]) => {
      // Handle both section ID formats
      const elementToReplace =
        document.getElementById(sectionId) || document.getElementById(`shopify-section-${sectionId}`);

      if (!elementToReplace) {
        console.warn(`[CartItems] Could not find element for section: ${sectionId}`);
        return;
      }

      // Parse the HTML to find the specific element we want to update
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get the sections to render configuration
      const sectionsToRender = this.getSectionsToRender();
      const sectionConfig = sectionsToRender.find((section) => section.id === sectionId);

      if (sectionConfig && sectionConfig.selector) {
        // Find the specific element in the new HTML
        const newContent = doc.querySelector(sectionConfig.selector);
        if (!newContent) return;
        // Find the corresponding element in the current DOM
        const targetElement = elementToReplace.querySelector(sectionConfig.selector);
        if (!targetElement) return;

        // GUARD: if the incoming HTML contains a <cart-drawer-items> and we're currently in one,
        // only replace *this* component's inner content to avoid nesting new instances.
        const incomingDrawerItems = newContent.querySelector('cart-drawer-items');
        const thisIsCartDrawerItems = this.tagName === 'CART-DRAWER-ITEMS';
        if (thisIsCartDrawerItems && incomingDrawerItems) {
          console.log('[CartItems] Safe-replacing inner HTML of current <cart-drawer-items>');
          this.innerHTML = incomingDrawerItems.innerHTML;
        } else {
          console.log(`[CartItems] Updating section ${sectionId} with selector ${sectionConfig.selector}`);
          targetElement.innerHTML = newContent.innerHTML;
        }
      } else {
        console.log(`[CartItems] Updating entire section ${sectionId}`);
        elementToReplace.innerHTML = html;

        if (sectionId === 'cart-icon-bubble') {
          console.log('[CartItems] Updated cart-icon-bubble section, checking cart count');
          const cartCount = elementToReplace.querySelector('.cart-count-bubble span');
          if (cartCount) {
            console.log('[CartItems] Cart count in updated section:', cartCount.textContent);
          }
        }
      }
    });

    console.log('[CartItems] Reinitializing quantity selectors after markup update');
    this.setupQuantitySelectors();
  }

  setupQuantitySelectors() {
    const quantitySelectors = this.querySelectorAll('.cart-quantity__select');
    console.log('[CartItems] Setting up quantity selectors:', {
      count: quantitySelectors.length,
      selectors: Array.from(quantitySelectors).map((s) => ({
        index: s.dataset.index,
        variantId: s.dataset.quantityVariantId,
        currentValue: s.value,
      })),
    });

    // Remove existing event listeners before adding new ones
    quantitySelectors.forEach((selector) => {
      const newSelector = selector.cloneNode(true);
      selector.parentNode.replaceChild(newSelector, selector);
      newSelector.addEventListener('change', this.handleQuantityChange.bind(this));
    });
  }

  setupCartUpdateListener() {
    // Subscribe to cart updates to check for three months free qualification
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      console.log('[CartItems] Cart update event received:', event);
      if (event?.sourceId && event.sourceId === this.instanceId) {
        return;
      }
      if (event.source === 'cart-items' || event.source === 'product-form') {
        // If the update came from a product form, we need to update the cart UI
        if (event.source === 'product-form') {
          const sectionsResponse = await fetch(
            `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
              .map((section) => section.id)
              .join(',')}`,
          );
          const sectionsData = await sectionsResponse.json();
          console.log('[CartItems] Sections data received:', sectionsData);

          const cartData = await this.getCartState();
          this.updateSectionsWithMarkup(sectionsData);
          this.refreshCartDrawer();
          this.updateCartUI(cartData);
        }

        // Check for three months free qualification
        await this.doesUserQualifyForThreeMonthsFree();
      }
    });

    // Setup debug buttons for development
    this.setupDebugButtons();
  }

  setupDebugButtons() {
    console.log('[CartItems] Debug: Setting up debug buttons');

    // Use event delegation to handle buttons that might be added dynamically
    document.addEventListener('click', async (event) => {
      if (event.target.id === 'CartDrawer-Debug-Clear') {
        event.preventDefault();
        console.log('[CartItems] Debug: Clear cart button clicked');

        try {
          await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          // Refresh the cart drawer
          const sectionsResponse = await fetch(
            `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
              .map((section) => section.id)
              .join(',')}`,
          );
          const sectionsData = await sectionsResponse.json();
          this.updateSectionsWithMarkup(sectionsData);
          this.refreshCartDrawer();

          console.log('[CartItems] Debug: Cart cleared successfully');
        } catch (error) {
          console.error('[CartItems] Debug: Error clearing cart:', error);
        }
      }

      if (event.target.id === 'CartDrawer-Debug-NewCart') {
        event.preventDefault();
        console.log('[CartItems] Debug: New cart ID button clicked');

        try {
          // Clear current cart
          await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          // Add a dummy item to get a new cart ID, then remove it
          const dummyResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            body: JSON.stringify({
              items: [
                {
                  id: 1, // Use a dummy variant ID
                  quantity: 1,
                },
              ],
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (dummyResponse.ok) {
            // Remove the dummy item
            await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            });

            // Refresh the cart drawer
            const sectionsResponse = await fetch(
              `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
                .map((section) => section.id)
                .join(',')}`,
            );
            const sectionsData = await sectionsResponse.json();
            this.updateSectionsWithMarkup(sectionsData);
            this.refreshCartDrawer();

            console.log('[CartItems] Debug: New cart ID generated successfully');
          }
        } catch (error) {
          console.error('[CartItems] Debug: Error generating new cart ID:', error);
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleQuantityChange(event) {
    const select = event.target;
    const line = select.dataset.index;
    const quantity = parseInt(select.value);
    const name = select.getAttribute('name');
    const variantId = select.dataset.quantityVariantId;

    this.updateQuantity(line, quantity, name, variantId);
  }

  async updateQuantity(line, quantity, name, variantId) {
    // Prevent recursive calls when removing associated free products
    if (this.isRemovingAssociatedProducts) {
      console.log('[CartItems] Skipping recursive call during associated product removal');
      return;
    }

    this.enableLoading(line);

    try {
      // Get current cart state before making changes
      const currentCart = await this.getCartState();
      const itemBeingModified = currentCart.items[line - 1];

      console.log('[CartItems] Updating quantity for item:', {
        line,
        quantity,
        variantId,
        item: itemBeingModified,
      });

      // Check if we're removing a qualifying product and need to remove associated free products
      if (quantity === 0 && itemBeingModified) {
        this.isRemovingAssociatedProducts = true;
        try {
          await this.removeAssociatedFreeProducts(itemBeingModified, currentCart);
        } finally {
          this.isRemovingAssociatedProducts = false;
        }
      }

      const sections = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections_url: window.location.pathname,
          sections: sections,
        }),
      });

      const cartData = await response.json();

      if (cartData.errors) {
        this.handleError(line, cartData.errors);
        return;
      }

      if (cartData.item_count === 0) {
        this.hideCartUI();
      }

      // Get fresh sections data
      const sectionsResponse = await fetch(
        `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
          .map((section) => section.id)
          .join(',')}`,
      );

      const sectionsData = await sectionsResponse.json();

      // Update UI in a single pass
      this.updateSectionsWithMarkup(sectionsData);
      this.updateCartUI(cartData);

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        sourceId: this.instanceId,
        cartData,
      });
    } catch (error) {
      console.error('Error updating cart:', error);
      this.updateLiveRegions(line, error);
    } finally {
      this.disableLoading(line);
    }
  }

  async getAssociatedProductsToRemove(removedItem, currentCart) {
    console.log('[CartItems] Getting associated products to remove for:', removedItem);

    // Check if the removed item has any promotion properties
    const promotionProperties = Object.keys(removedItem.properties || {}).filter((key) =>
      key.startsWith('_free-product-addon-'),
    );

    if (promotionProperties.length === 0) {
      console.log('[CartItems] No promotion properties found on removed item');
      return [];
    }

    console.log('[CartItems] Found promotion properties:', promotionProperties);

    // Get all promotion blocks to find associated free products
    const promotionBlocks = document.querySelectorAll('.metaobject-promotion-block');
    const associatedLineNumbers = [];

    promotionBlocks.forEach((block) => {
      try {
        const configScript = block.querySelector('script[data-promotion-config]');
        const config = configScript ? JSON.parse(configScript.textContent) : {};

        if (config.enabled && config.actionType === 'add_product' && config.freeProductId) {
          const promotionProperty = `_free-product-addon-${config.promotionName || 'promotion'}`;

          if (promotionProperties.includes(promotionProperty)) {
            console.log(`[CartItems] Found associated free product for ${config.promotionName}:`, config.freeProductId);

            // Find all cart items with this variant ID
            currentCart.items.forEach((item, index) => {
              if (item.variant_id === config.freeProductId) {
                const lineNumber = index + 1; // Line numbers are 1-based
                associatedLineNumbers.push(lineNumber);
                console.log(`[CartItems] Adding line ${lineNumber} (${item.product_title}) to removal list`);
              }
            });
          }
        }
      } catch (error) {
        console.warn('[CartItems] Error parsing promotion config:', error);
      }
    });

    return associatedLineNumbers;
  }

  async removeMultipleItems(lineNumbers) {
    console.log('[CartItems] Removing multiple items:', lineNumbers);

    // Sort line numbers in descending order to avoid index shifting issues
    const sortedLineNumbers = [...lineNumbers].sort((a, b) => b - a);
    console.log('[CartItems] Removing items in order (highest first):', sortedLineNumbers);

    try {
      // Remove items one by one, starting with the highest line number
      for (const lineNumber of sortedLineNumbers) {
        console.log(`[CartItems] Removing line ${lineNumber}`);

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0,
            sections_url: window.location.pathname,
            sections: this.getSectionsToRender()
              .map((section) => section.id)
              .join(','),
          }),
        });

        const cartData = await response.json();
        console.log(`[CartItems] Removed line ${lineNumber}, cart now has ${cartData.items?.length || 0} items`);

        if (cartData.errors) {
          console.error(`[CartItems] Error removing line ${lineNumber}:`, cartData.errors);
        }
      }

      // Get final cart state
      const finalCart = await this.getCartState();
      console.log('[CartItems] Final cart state:', finalCart);

      // Get fresh sections data
      const sectionsResponse = await fetch(
        `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
          .map((section) => section.id)
          .join(',')}`,
      );
      const sectionsData = await sectionsResponse.json();

      // Update UI
      this.updateSectionsWithMarkup(sectionsData);
      this.updateCartUI(finalCart);

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        sourceId: this.instanceId,
        cartData: finalCart,
      });

      console.log('[CartItems] Successfully removed multiple items');
    } catch (error) {
      console.error('[CartItems] Error removing multiple items:', error);
    }
  }

  async removeAssociatedFreeProducts(removedItem, currentCart) {
    console.log('[CartItems] Checking for associated free products to remove:', removedItem);

    // Check if the removed item has any promotion properties
    const promotionProperties = Object.keys(removedItem.properties || {}).filter((key) =>
      key.startsWith('_free-product-addon-'),
    );

    if (promotionProperties.length === 0) {
      console.log('[CartItems] No promotion properties found on removed item');
      return;
    }

    console.log('[CartItems] Found promotion properties:', promotionProperties);

    // Get all promotion blocks to find associated free products
    const promotionBlocks = document.querySelectorAll('.metaobject-promotion-block');
    const freeProductsToRemove = [];

    promotionBlocks.forEach((block) => {
      try {
        const configScript = block.querySelector('script[data-promotion-config]');
        const config = configScript ? JSON.parse(configScript.textContent) : {};

        if (config.enabled && config.actionType === 'add_product' && config.freeProductId) {
          const promotionProperty = `_free-product-addon-${config.promotionName || 'promotion'}`;

          if (promotionProperties.includes(promotionProperty)) {
            console.log(`[CartItems] Found associated free product for ${config.promotionName}:`, config.freeProductId);
            freeProductsToRemove.push({
              variantId: config.freeProductId,
              promotionName: config.promotionName || 'promotion',
            });
          }
        }
      } catch (error) {
        console.warn('[CartItems] Error parsing promotion config:', error);
      }
    });

    // Remove associated free products using a single cart update request
    if (freeProductsToRemove.length > 0) {
      const updates = [];

      for (const freeProduct of freeProductsToRemove) {
        const freeProductItems = currentCart.items.filter((item) => item.variant_id === freeProduct.variantId);

        console.log(
          `[CartItems] Removing ${freeProductItems.length} instances of ${freeProduct.promotionName} free product`,
        );

        for (const freeProductItem of freeProductItems) {
          const itemIndex = currentCart.items.findIndex(
            (item) => item.variant_id === freeProductItem.variant_id && item.id === freeProductItem.id,
          );

          if (itemIndex !== -1) {
            console.log(`[CartItems] Adding free product at index ${itemIndex + 1} to removal list`);
            updates.push({
              line: itemIndex + 1,
              quantity: 0,
            });
          }
        }
      }

      // Send all free product removals in a single request
      if (updates.length > 0) {
        console.log(`[CartItems] Removing ${updates.length} free products in single request:`, updates);

        try {
          const response = await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({ updates }),
          });

          const cartData = await response.json();

          if (cartData.errors) {
            console.error('[CartItems] Error removing free products:', cartData.errors);
          } else {
            console.log('[CartItems] Successfully removed associated free products');
          }
        } catch (error) {
          console.error('[CartItems] Error removing free products:', error);
        }
      }
    }
  }

  async doesUserQualifyForThreeMonthsFree() {
    const cart = await this.getCartState();
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Check for legacy free product addon
    const doesAnyCartItemHaveLegacyTag = cart.items.some((item) => item.properties['_free-product-addon'] === 'true');

    // Check for new promotion block free product addons (simplified)
    const promotionBlocks = document.querySelectorAll('.metaobject-promotion-block');
    const activePromotions = [];

    promotionBlocks.forEach((block) => {
      try {
        // Look for the JSON script tag with promotion config
        const configScript = block.querySelector('script[data-promotion-config]');
        const config = configScript ? JSON.parse(configScript.textContent) : {};
        console.log('[CartItems] Processing promotion block:', config.promotionName, config);

        if (config.enabled && config.actionType === 'add_product' && config.freeProductId) {
          const hasQualifyingItem = cart.items.some(
            (item) => item.properties[`_free-product-addon-${config.promotionName || 'promotion'}`] === 'true',
          );

          console.log(`[CartItems] Promotion ${config.promotionName}: hasQualifyingItem = ${hasQualifyingItem}`);

          if (hasQualifyingItem) {
            activePromotions.push({
              variantId: config.freeProductId,
              quantity: config.freeProductQuantity || 1,
              promotionName: config.promotionName || 'promotion',
            });
            console.log(`[CartItems] Added promotion ${config.promotionName} to active promotions`);
          }
        } else {
          console.log(`[CartItems] Promotion ${config.promotionName} skipped:`, {
            enabled: config.enabled,
            actionType: config.actionType,
            hasFreeProductId: !!config.freeProductId,
          });
        }
      } catch (error) {
        console.warn('[CartItems] Error parsing promotion config:', error);
      }
    });

    // Handle legacy free product addon (if still configured in theme settings)
    if (doesAnyCartItemHaveLegacyTag) {
      // Check if legacy free product is still configured
      const legacyFreeProduct = window.Shopify?.theme?.settings?.free_product;
      if (legacyFreeProduct?.selected_or_first_available_variant?.id) {
        const legacyVariantId = legacyFreeProduct.selected_or_first_available_variant.id;
        const doesCartAlreadyHaveLegacyFree = cart.items.some(
          (item) => item.variant_id === parseFloat(legacyVariantId),
        );

        if (!doesCartAlreadyHaveLegacyFree) {
          console.log('[CartItems] Adding legacy three months free product to cart');
          await this.addThreeMonthsFreeToCart(legacyVariantId, 1, 'legacy');
        }
      }
    } else {
      // Remove legacy free product if no qualifying items
      const legacyFreeProduct = window.Shopify?.theme?.settings?.free_product;
      if (legacyFreeProduct?.selected_or_first_available_variant?.id) {
        const legacyVariantId = legacyFreeProduct.selected_or_first_available_variant.id;
        const doesCartAlreadyHaveLegacyFree = cart.items.some(
          (item) => item.variant_id === parseFloat(legacyVariantId),
        );

        if (doesCartAlreadyHaveLegacyFree) {
          console.log('[CartItems] Removing legacy three months free product from cart');
          const freeMonthsIndex = cart.items.findIndex((item) => item.variant_id === parseFloat(legacyVariantId));

          if (freeMonthsIndex !== -1) {
            this.updateQuantity(freeMonthsIndex + 1, 0, 'updates[]', parseFloat(legacyVariantId));
          }
        }
      }
    }

    // Handle new promotion block free products
    console.log('[CartItems] Active promotions to process:', activePromotions);
    for (const promotion of activePromotions) {
      // Count qualifying items for this promotion
      const qualifyingItems = cart.items.filter(
        (item) => item.properties[`_free-product-addon-${promotion.promotionName || 'promotion'}`] === 'true',
      );
      const qualifyingQuantity = qualifyingItems.reduce((sum, item) => sum + item.quantity, 0);

      // Count existing free products for this promotion
      const existingFreeProducts = cart.items.filter((item) => item.variant_id === promotion.variantId);
      const existingFreeQuantity = existingFreeProducts.reduce((sum, item) => sum + item.quantity, 0);

      console.log(`[CartItems] Processing promotion ${promotion.promotionName}:`, {
        variantId: promotion.variantId,
        qualifyingQuantity,
        existingFreeQuantity,
        shouldHaveQuantity: qualifyingQuantity * (promotion.quantity || 1),
      });

      const shouldHaveQuantity = qualifyingQuantity * (promotion.quantity || 1);

      if (shouldHaveQuantity > existingFreeQuantity) {
        // Add missing free products
        const quantityToAdd = shouldHaveQuantity - existingFreeQuantity;
        console.log(`[CartItems] Adding ${quantityToAdd} ${promotion.promotionName} free products to cart`);
        await this.addThreeMonthsFreeToCart(promotion.variantId, quantityToAdd, promotion.promotionName);
      } else if (shouldHaveQuantity < existingFreeQuantity) {
        // Remove excess free products
        const quantityToRemove = existingFreeQuantity - shouldHaveQuantity;
        console.log(
          `[CartItems] Removing ${quantityToRemove} excess ${promotion.promotionName} free products from cart`,
        );

        // Find and remove excess free products
        let remainingToRemove = quantityToRemove;
        for (let i = cart.items.length - 1; i >= 0 && remainingToRemove > 0; i--) {
          const item = cart.items[i];
          if (item.variant_id === promotion.variantId) {
            const removeFromThisItem = Math.min(remainingToRemove, item.quantity);
            if (removeFromThisItem === item.quantity) {
              // Remove entire item
              this.updateQuantity(i + 1, 0, 'updates[]', item.variant_id);
            } else {
              // Reduce quantity
              this.updateQuantity(i + 1, item.quantity - removeFromThisItem, 'updates[]', item.variant_id);
            }
            remainingToRemove -= removeFromThisItem;
          }
        }
      } else {
        console.log(`[CartItems] ${promotion.promotionName} free product quantity is correct`);
      }
    }

    // Remove promotion free products that are no longer qualified
    const allPromotionVariantIds = activePromotions.map((p) => p.variantId);
    const legacyFreeProduct = window.Shopify?.theme?.settings?.free_product;
    const legacyVariantId = legacyFreeProduct?.selected_or_first_available_variant?.id;

    console.log('[CartItems] Checking for free products to remove:', {
      allPromotionVariantIds,
      legacyVariantId,
      doesAnyCartItemHaveLegacyTag,
      activePromotionsCount: activePromotions.length,
    });

    // Remove legacy free products if no qualifying items
    if (!doesAnyCartItemHaveLegacyTag && legacyVariantId) {
      const legacyFreeProducts = cart.items.filter((item) => item.variant_id === parseFloat(legacyVariantId));
      if (legacyFreeProducts.length > 0) {
        console.log(`[CartItems] Removing ${legacyFreeProducts.length} legacy free products (no qualifying items)`);
        legacyFreeProducts.forEach((item, index) => {
          const cartIndex = cart.items.findIndex(
            (cartItem) => cartItem.variant_id === item.variant_id && cartItem.id === item.id,
          );
          if (cartIndex !== -1) {
            this.updateQuantity(cartIndex + 1, 0, 'updates[]', item.variant_id);
          }
        });
      }
    }

    // Remove promotion free products that are no longer qualified
    cart.items.forEach((item, index) => {
      // Check if this is a promotion free product that should be removed
      const isPromotionFreeProduct =
        allPromotionVariantIds.includes(item.variant_id) &&
        !activePromotions.some((p) => p.variantId === item.variant_id);

      console.log(`[CartItems] Checking cart item ${index}:`, {
        variantId: item.variant_id,
        productTitle: item.product_title,
        isPromotionFreeProduct,
        shouldRemove: isPromotionFreeProduct,
      });

      if (isPromotionFreeProduct) {
        console.log(
          `[CartItems] Removing orphaned free product variant ${item.variant_id} (${item.product_title}) from cart`,
        );
        this.updateQuantity(index + 1, 0, 'updates[]', item.variant_id);
      }
    });

    return doesAnyCartItemHaveLegacyTag || activePromotions.length > 0;
  }

  async addThreeMonthsFreeToCart(variantId, quantity, promotionName = 'legacy') {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
              properties: {
                '_is-free-product': 'true',
                '_free-product-type': promotionName,
              },
            },
          ],
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseData = await response.json();

      // Get fresh sections data
      const sectionsResponse = await fetch(
        `${window.Shopify.routes.root}?sections=${this.getSectionsToRender()
          .map((section) => section.id)
          .join(',')}`,
      );
      const sectionsData = await sectionsResponse.json();

      // Get updated cart state
      const cartData = await this.getCartState();

      // Update UI with new data
      this.updateSectionsWithMarkup(sectionsData);
      this.refreshCartDrawer();
      this.updateCartUI(cartData);

      return {
        sections: sectionsData,
        cartData: cartData,
        id: variantId.toString(),
      };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return null;
    }
  }

  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const response = await fetch(`${routes.cart_url}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newItems = doc.querySelector('.cart-drawer__items');
    const currentItems = document.querySelector('.cart-drawer__items');
    if (newItems && currentItems) {
      currentItems.innerHTML = newItems.innerHTML;
      this.setupQuantitySelectors();
    }

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = document.querySelector('.cart-drawer__footer');
    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }
  }

  async getCartState() {
    const response = await fetch(`${routes.cart_url}.js`);
    return response.json();
  }

  updateCartUI(cart, forceShowUI = false) {
    console.log('[CartItems] Updating cart UI:', {
      cart,
      cartItemCount: cart?.items?.length,
      currentUrl: window?.location?.href,
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const button = document.querySelector('#CartDrawer-Checkout');
    const cartCounts = document.querySelectorAll('.cart-count-bubble span');

    // Only update cart count if it hasn't been updated by sections
    // Check if the cart count is already correct
    const firstCartCount = cartCounts[0];
    if (firstCartCount && firstCartCount.textContent !== cart.item_count.toString()) {
      console.log('[CartItems] Updating cart count from', firstCartCount.textContent, 'to', cart.item_count);
      cartCounts.forEach((cartCount) => {
        cartCount.textContent = cart.item_count;
        cartCount.classList.toggle('hidden', cart.item_count === 0);
      });
    } else {
      console.log('[CartItems] Cart count already up to date:', cart.item_count);
    }

    if (cartDrawer) {
      if (forceShowUI || cart.items.length > 0 || cart.variant_id) {
        button.disabled = false;
        cartDrawer.classList.remove('is-empty');
      } else {
        button.disabled = true;
        cartDrawer.classList.add('is-empty');
      }
    }
  }

  hideCartUI() {
    const cartDrawer = document.querySelector('cart-drawer');
    const button = document.querySelector('#CartDrawer-Checkout');

    button.disabled = true;
    cartDrawer.classList.add('is-empty');
  }

  handleError(line, errors) {
    const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
    if (quantityElement) {
      quantityElement.value = quantityElement.querySelector('input')?.value || quantityElement.dataset.maxQuantity;
    }
    this.updateLiveRegions(line, errors);
  }

  enableLoading(line) {
    const mainCartItems = document.querySelector('.cart-drawer__items');
    mainCartItems?.classList.add('cart__items--disabled');

    const spinner = this.querySelector(`#CartDrawer-Item-${line} .loading__spinner`);
    spinner?.classList.remove('hidden');

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.querySelector('.cart-drawer__items');
    mainCartItems?.classList.remove('cart__items--disabled');

    const spinner = this.querySelector(`#CartDrawer-Item-${line} .loading__spinner`);
    spinner?.classList.add('hidden');
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sectionId = this.dataset.sectionId;
    return [
      {
        id: sectionId,
        selector: '.drawer__inner',
        section: 'cart-drawer-items,.cart-drawer__drawer-footer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
